/**
 * Playback Experience helpers.
 *
 * Players should use resolvePlaybackExperience() instead of hardcoding
 * which content types show a cinematic intro. Future layers
 * (organization / creator / seasonal / white-label) can extend intro_layers.
 */

import configuration from "react-global-configuration";

export function getSitePlaybackExperience() {
  try {
    return configuration.get("configData.playback_experience") || null;
  } catch (e) {
    return null;
  }
}

/**
 * Prefer per-video API payload; fall back to site config + local context.
 */
export function resolvePlaybackExperience(videoDetails, options = {}) {
  if (videoDetails && videoDetails.playback_experience) {
    const pe = videoDetails.playback_experience;
    // If caller knows this is a trailer, re-evaluate against policy when API wasn't told.
    if (options.isTrailer && pe.policy) {
      const should =
        !!pe.enabled &&
        !!pe.cinematic_intro &&
        !!pe.cinematic_intro.enabled &&
        !!pe.cinematic_intro.url &&
        !!pe.policy.video_trailers;
      return {
        ...pe,
        playback_context: "video_trailers",
        should_play_intro: should,
        intro_layers: should ? pe.intro_layers || [] : [],
      };
    }
    return pe;
  }

  const site = getSitePlaybackExperience();
  if (!site) {
    return {
      version: 1,
      enabled: false,
      should_play_intro: false,
      cinematic_intro: { enabled: false, url: "", skip_mode: "disabled", skip_after_seconds: null },
      policy: {},
      intro_layers: [],
      playback_context: options.context || "public_free_videos",
    };
  }

  let context = options.context || "public_free_videos";
  if (options.isTrailer) context = "video_trailers";
  else if (options.isLive) context = "live_streams";
  else if (options.isCreator) context = "creator_videos";
  else if (options.isPpv) context = "digital_purchases_ppv";
  else if (options.requiresSubscription) context = "subscription_videos";

  const intro = site.cinematic_intro || {};
  const policy = site.policy || {};
  const should =
    !!site.enabled &&
    !!intro.enabled &&
    !!intro.url &&
    !!policy[context];

  return {
    ...site,
    playback_context: context,
    should_play_intro: should,
    intro_layers: should
      ? site.intro_layers && site.intro_layers.length
        ? site.intro_layers
        : [
            {
              type: "cinematic",
              source: "site",
              url: intro.url,
              priority: 100,
              skip_mode: intro.skip_mode,
              skip_after_seconds: intro.skip_after_seconds,
            },
          ]
      : [],
  };
}

export function getPrimaryIntroLayer(playbackExperience) {
  if (!playbackExperience || !playbackExperience.should_play_intro) {
    return null;
  }
  const layers = playbackExperience.intro_layers || [];
  if (!layers.length) {
    const intro = playbackExperience.cinematic_intro;
    if (intro && intro.url) {
      return {
        type: "cinematic",
        url: intro.url,
        skip_mode: intro.skip_mode,
        skip_after_seconds: intro.skip_after_seconds,
      };
    }
    return null;
  }
  return [...layers].sort((a, b) => (b.priority || 0) - (a.priority || 0))[0];
}
